// usePagedList.js
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * usePagedList
 *
 * A list that loads its first page, then appends the next one each time the
 * reader asks. The API side differs by endpoint -- awards page by `page` and
 * say `has_next`, trades and companies page by `offset` and give a `total` --
 * so the caller supplies fetchPage(pageIndex), which resolves to
 * { rows, hasMore, meta }. pageIndex starts at 0.
 *
 * - A change in deps (a new feed, window or search) starts over from page 0,
 *   and any answer still in flight for the old one is dropped when it lands.
 * - keyOf, when given, drops a row already on the list. Between two pages of
 *   the newest awards, new awards can arrive and push rows the reader has
 *   already seen onto the next page.
 * - Passing null for fetchPage leaves the list empty and idle: a search box
 *   with fewer than two characters typed, say.
 * - A failed first page is `error`; a failed later page is `moreError`, and
 *   the rows already shown stay.
 */
export default function usePagedList(fetchPage, deps, { keyOf } = {}) {
  const [state, setState] = useState({
    rows: [],
    meta: null,
    page: 0,
    hasMore: false,
    loading: Boolean(fetchPage),
    loadingMore: false,
    error: null,
    moreError: null,
  });

  // Which request generation is current. Anything older is dropped on arrival.
  const generation = useRef(0);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const stateRef = useRef(state);
  stateRef.current = state;

  const merge = useCallback(
    (current, incoming) => {
      if (!keyOf) return current.concat(incoming);
      const seen = new Set(current.map(keyOf));
      return current.concat(incoming.filter((row) => !seen.has(keyOf(row))));
    },
    [keyOf]
  );

  useEffect(() => {
    const id = ++generation.current;
    const load = fetchRef.current;

    if (!load) {
      setState({
        rows: [], meta: null, page: 0, hasMore: false,
        loading: false, loadingMore: false, error: null, moreError: null,
      });
      return;
    }

    setState({
      rows: [], meta: null, page: 0, hasMore: false,
      loading: true, loadingMore: false, error: null, moreError: null,
    });

    load(0)
      .then((result) => {
        if (id !== generation.current) return;
        setState({
          rows: Array.isArray(result?.rows) ? result.rows : [],
          meta: result?.meta ?? null,
          page: 0,
          hasMore: Boolean(result?.hasMore),
          loading: false,
          loadingMore: false,
          error: null,
          moreError: null,
        });
      })
      .catch((error) => {
        if (id !== generation.current) return;
        setState((current) => ({ ...current, loading: false, error }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = useCallback(() => {
    const current = stateRef.current;
    const load = fetchRef.current;
    if (!load || current.loading || current.loadingMore || !current.hasMore) return;

    const id = generation.current;
    const nextPage = current.page + 1;
    setState((value) => ({ ...value, loadingMore: true, moreError: null }));

    load(nextPage)
      .then((result) => {
        if (id !== generation.current) return;
        const incoming = Array.isArray(result?.rows) ? result.rows : [];
        setState((value) => ({
          ...value,
          rows: merge(value.rows, incoming),
          meta: result?.meta ?? value.meta,
          page: nextPage,
          // An empty page ends the list, whatever the flag says.
          hasMore: Boolean(result?.hasMore) && incoming.length > 0,
          loadingMore: false,
        }));
      })
      .catch((moreError) => {
        if (id !== generation.current) return;
        setState((value) => ({ ...value, loadingMore: false, moreError }));
      });
  }, [merge]);

  return { ...state, loadMore };
}